/**
 * ShareQRModal.jsx — Modale de partage d'un événement via QR code.
 * Génère un token 7j, affiche le QR + URL courte + boutons copier/imprimer.
 */
import React, { useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { X, Copy, Printer, ExternalLink, RefreshCw } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';

export default function ShareQRModal({ open, onClose, event }) {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && event?.id) {
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.id]);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.persoShareEvent(event.id);
      setShare(r);
    } catch (e) { toast.error(e.message || 'Erreur'); }
    finally { setLoading(false); }
  };

  if (!open) return null;

  const shareUrl = share ? `${window.location.origin}${share.url_path}` : '';

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Lien copié');
    } catch { toast.error('Copie impossible'); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="share-qr-modal">
      <div className="w-full max-w-md bg-[#0d0d0e] border border-white/10 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-rose-400">Partager le bilan</div>
            <h2 className="text-lg font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Fin de cours</h2>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-6 text-center">
          <p className="text-sm text-white/60 mb-4">Ton élève scanne ce QR pour voir le bilan de la séance + les prochains cours à voir. Lien valable <strong className="text-white">7 jours</strong>.</p>

          {loading && (
            <div className="h-56 flex items-center justify-center text-white/40 text-sm"><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Génération du QR…</div>
          )}

          {!loading && share && (
            <>
              <div className="mx-auto inline-block p-4 bg-white rounded-2xl" data-testid="qr-canvas-wrap">
                <QRCodeCanvas value={shareUrl} size={200} level="M" includeMargin={false} />
              </div>
              <div className="mt-4">
                <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-1">Lien court</div>
                <div className="font-mono text-sm text-white/85 break-all" data-testid="share-url">{shareUrl}</div>
              </div>
              <div className="flex items-center justify-center gap-2 mt-4">
                <button onClick={copyUrl} className="px-3 py-2 rounded-lg border border-white/15 text-white/80 hover:bg-white/5 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="btn-copy-url">
                  <Copy size={12} /> Copier
                </button>
                <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-2 rounded-lg border border-white/15 text-white/80 hover:bg-white/5 text-xs font-semibold inline-flex items-center gap-1.5">
                  <ExternalLink size={12} /> Aperçu
                </a>
                <button onClick={() => window.print()} className="px-3 py-2 rounded-lg border border-white/15 text-white/80 hover:bg-white/5 text-xs font-semibold inline-flex items-center gap-1.5">
                  <Printer size={12} /> Imprimer
                </button>
              </div>
              {share.expires_at && (
                <div className="mt-3 text-[11px] text-white/40">
                  Expire le {new Date(share.expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
